import React from 'react';
import PropTypes from 'prop-types';
import SectionTitle from '../../../components/SectionTitle.js';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';
import { AuthAPI } from '../../../api';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';

const searchFields = ['email', 'username', 'first_name', 'last_name'];
const toStr = (value) => value === undefined || value === null ? '' : value;
const userLabel = (u) => u ? `${u.username} | ${u.email} | ${toStr(u.first_name)} ${toStr(u.last_name)}` : '';
const userList = (users) => users.map(u => {
  return {
    category: u.is_provider ? 'Providers' : 'Participants',
    ...u,
  };
}).sort((u1, u2) => u2.category.localeCompare(u1.category));

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    '& .MuiAutocomplete-root': {
      width: '100%',
    },
  },
  input: {
    '& .MuiAutocomplete-inputRoot': {
      backgroundColor: theme.palette.common.paleGrey,
      paddingTop: 0,
    },
    '& .MuiFilledInput-underline:after': {
      borderBottom: 0,
    },
    '& .MuiFilledInput-underline:before': {
      borderBottom: 0,
    },
  },
  search: {
    backgroundColor: theme.palette.common.paleGrey,
  },
  spinner: {
    padding: 5,
  },
}));

// lastQuery global is used to ensure we only update the options in response to the
// most recent request made.
let lastQuery = undefined;

const UserSearch = ({onEnter, title=null}) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = React.useState('');
  const [value, setValue] = React.useState();
  const [options, setOptions] = React.useState([]);
  const [focus, setFocus] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const open = focus && (options.length > 1 || (options.length === 1 && value !== inputValue));

  React.useEffect(() => {
    if (inputValue.length < 2) return;
    lastQuery = inputValue;
    setBusy(true);
    AuthAPI.userAutocomplete({query:inputValue, fields:searchFields, limit:20}).then(r => {
      // avoid updating options if inputValue has since changed
      if (inputValue === lastQuery) {
        setOptions(userList(r.payload.users));
        setBusy(false);
      }
    });
  }, [inputValue]);

  const renderInput = (params) => {
    return (
      <Box display="flex" alignItems="center" className={classes.search}>
        <Box flexGrow={1}>
          <TextField
            className={classes.input}
            {...params}
            placeholder="Search for users by username, email or name"
            variant="filled"
          />
        </Box>
        <Box className={classes.spinner}>
          {busy && <CircularProgress size={20} thickness={5}/>}
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="md">
      <div className={classes.root}>
        {title !== null ? (<SectionTitle title={title} />) : null}
        <Autocomplete
          options={options}
          getOptionLabel={userLabel}
          groupBy={u => u.category}
          open={open}
          autoComplete
          autoHighlight
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={(event, value) => {
            setValue(value);
            onEnter(value);
          }}
          popupIcon={null}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          value={value}
          renderInput={renderInput}
        />

      </div>
    </Container>
  );
};

UserSearch.propTypes = {
  value: PropTypes.string,
  onEnter: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default UserSearch;
